import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function ImpactQuality({ API_URL, getAuthHeader, refreshKey }) {
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [dupes, setDupes] = useState({ groups: [] });
  const [overview, setOverview] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [sr, er, dr, or] = await Promise.all([
        fetch(`${API_URL}/api/impact/settings`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/events`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/duplicates`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/overview`, { headers: getAuthHeader() }),
      ]);
      if (sr.ok) setSettings(await sr.json());
      if (er.ok) setEvents(await er.json());
      if (dr.ok) setDupes(await dr.json());
      if (or.ok) setOverview(await or.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const saveFyStart = async (month) => {
    const r = await fetch(`${API_URL}/api/impact/settings`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ fiscal_year_start_month: month })
    });
    if (r.ok) { setSettings(await r.json()); toast.success('Fiscal year start updated'); }
  };

  const needsClass = events.filter(e => !e.program_key);
  const undated = events.filter(e => !e.date && e.status !== 'canceled');
  const attendanceCoverage = overview?.kpis?.attendance_recorded_pct;

  const Item = ({ ok, label, detail, testid }) => (
    <div className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      {ok ? <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: BRAND.lime }} /> : <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />}
      <div>
        <p className="text-sm font-semibold" style={{ color: BRAND.navy }}>{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{detail}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="impact-quality">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-3" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Reporting settings</h3>
        <div className="flex items-center gap-3 text-sm">
          <label className="font-medium text-slate-600">Fiscal year starts:</label>
          <select value={settings?.fiscal_year_start_month ?? 10} onChange={e => saveFyStart(parseInt(e.target.value))}
            data-testid="impact-fy-select" className="border border-slate-300 rounded-lg px-3 py-2 bg-white">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m} 1</option>)}
          </select>
          <span className="text-xs text-slate-400">User-facing dates use America/New_York.</span>
        </div>
      </div>

      <div>
        <h3 className="font-bold mb-3" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Data quality checks</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Item ok={needsClass.length === 0} testid="dq-classification"
            label={needsClass.length === 0 ? 'All events classified to a program' : `${needsClass.length} event(s) need a program classification`}
            detail={needsClass.length ? `Classify in Trainings & Events: ${needsClass.map(e => e.title).slice(0, 3).join('; ')}${needsClass.length > 3 ? '…' : ''}` : 'Every training and activity has a primary program.'} />
          <Item ok={dupes.groups.length === 0} testid="dq-duplicates"
            label={dupes.groups.length === 0 ? 'No duplicate participant candidates' : `${dupes.groups.length} duplicate candidate group(s) to review`}
            detail="Name matches are suggestions only — merging always requires manual review." />
          <Item ok={attendanceCoverage !== null && attendanceCoverage > 0} testid="dq-attendance"
            label={`Attendance recorded for ${attendanceCoverage ?? 0}% of participations`}
            detail="Historical registrations without rosters show as 'Not recorded' — never counted as no-shows. Attendance marking arrives in Phase 2." />
          <Item ok={undated.length === 0} testid="dq-undated"
            label={undated.length === 0 ? 'All events have dates' : `${undated.length} ongoing / undated activity record(s)`}
            detail="Undated records (e.g., rolling applications) are excluded from monthly trends but included in totals." />
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-xs text-slate-500 leading-relaxed">
        <p className="font-bold text-slate-600 mb-2">Counting rules in effect (Phase 1)</p>
        <ul className="list-disc ml-4 space-y-1">
          <li>A person is counted once in unique totals regardless of how many sessions or programs they joined; program breakdowns disclose overlap separately.</li>
          <li>One person attending three sessions = 1 unique attendee, 3 person-session participations.</li>
          <li>Missing historical attendance is shown as “Not recorded”, never as absent; unknown data is never treated as zero.</li>
          <li>Participation aggregates by event/session date; certificates aggregate by issue date.</li>
          <li>Merges are manual and reversible; email matches are suggested, never automatic for ambiguous records.</li>
        </ul>
      </div>
    </div>
  );
}
